import * as React from "react";
import { Card, CardBody, CardHeader, PageContainer } from "../components/Card";
import { PageHeader, SectionHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { Modal, ModalBody } from "../components/Modal";
import { ConfirmModal } from "../components/ConfirmModal";
import { Field, Input, Select, Textarea, Checkbox } from "../components/Form";
import { Table, Thead, Tbody, Tr, Th, Td, TdActions } from "../components/Table";
import { Badge, StatusBadge } from "../components/Badge";
import { Banner, EmptyState, ListStates } from "../components/States";
import { IdBadge } from "../components/Detail";
import { Tabs } from "../components/Tabs";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { ReportTemplateModel } from "../api/APIClient";
import { ModulePicker } from "../activities/ModulePicker";
import { useStudentNames } from "../students/StudentDirectory";
import * as Management from "../management/ManagementStore";
import * as Store from "./ReportsStore";

/**
 * Report templates, and running them.
 *
 * Two tabs, because they are two different jobs: authoring the HTML a report renders from, and
 * producing an actual report for a set of students.
 *
 * The rendered output reads only stored `MarkResult` rows. Nothing on this screen can trigger a
 * calculation, and nothing should ever be added that does - the legacy system's separate
 * report-time calculation path is exactly what this rewrite exists to avoid.
 */

const PAPER_SIZES = ["A4", "A3", "Letter", "Legal"];

export const ReportsIndex: React.FC = () => {
    const [tab, setTab] = React.useState<"run" | "templates">("run");
    const canManage = usePermission(Permissions.REPORTS_WRITE);

    return (
        <PageContainer>
            <PageHeader
                title="Reports"
                icon={["fad", "file-lines"]}
                description="Rendered from stored results only - a report never recalculates a mark."
            />

            <Tabs
                active={tab}
                onChange={(k) => setTab(k as "run" | "templates")}
                tabs={[
                    { key: "run", label: "Run a report", icon: ["fad", "print"] },
                    { key: "templates", label: "Templates", icon: ["fad", "file-code"], visible: canManage },
                ]}
            />

            {tab === "run" ? <RunReport /> : <TemplatesSection />}
        </PageContainer>
    );
};

// ------------------------------------------------------------------- running

const RunReport: React.FC = () => {
    const toast = useToast();

    const [templateID, setTemplateID] = React.useState("");
    const [moduleID, setModuleID] = React.useState("");
    const [teachingGroupID, setTeachingGroupID] = React.useState("");
    const [finalizedOnly, setFinalizedOnly] = React.useState(true);
    const [dataSet, setDataSet] = React.useState<Store.ReportDataSet | null>(null);
    const [playwrightMissing, setPlaywrightMissing] = React.useState(false);

    const templates = useAsync(() => Store.listTemplates(), [], { initial: [] });
    const teachingGroups = useAsync(() => Management.listTeachingGroups(), [], { initial: [] });

    const groupOptions = React.useMemo(
        () => (teachingGroups.data ?? []).filter((g) => !moduleID || g.qualificationModuleID === moduleID),
        [teachingGroups.data, moduleID]
    );

    const request = () => ({
        templateID,
        qualificationModuleID: moduleID,
        teachingGroupID: teachingGroupID || undefined,
        finalizedOnly,
    });

    const ready = !!(templateID && moduleID);

    const preview = useAction(async () => {
        setPlaywrightMissing(false);
        setDataSet(await Store.previewReport(request()));
    });

    const html = useAction(async () => {
        setPlaywrightMissing(false);
        Store.openInNewTab(await Store.renderHtml(request()));
    });

    const pdf = useAction(async () => {
        setPlaywrightMissing(false);
        try {
            Store.saveFile(await Store.renderPdf(request()), "report.pdf");
            toast.success("PDF downloaded.");
        } catch (e) {
            if (Store.isPlaywrightMissing(e)) {
                setPlaywrightMissing(true);
                return;
            }
            throw e;
        }
    });

    const students = dataSet?.students ?? [];
    const nameOf = useStudentNames(students.map((s) => s.studentID));

    return (
        <>
            <Card className="mb-6">
                <CardHeader>
                    <SectionHeader title="What to render" />
                </CardHeader>
                <CardBody>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Field label="Template" required className="mb-0">
                            <Select value={templateID} placeholder="Select a template" onChange={(e) => setTemplateID(e.target.value)}>
                                {(templates.data ?? []).map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <ModulePicker value={moduleID} onChange={setModuleID} />

                        <Field label="Teaching group" className="mb-0" hint="Leave empty to include everyone taking the module.">
                            <Select
                                value={teachingGroupID}
                                placeholder="Everyone in the module"
                                onChange={(e) => setTeachingGroupID(e.target.value)}
                            >
                                {groupOptions.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.name}
                                    </option>
                                ))}
                            </Select>
                        </Field>

                        <div className="flex items-end pb-1">
                            <Checkbox
                                label="Finalized marks only"
                                hint="On by default, so a report never shows a mark still being worked on."
                                checked={finalizedOnly}
                                onChange={(e) => setFinalizedOnly(e.target.checked)}
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button variant="outline-primary" icon={["fad", "eye"]} onClick={preview.run} loading={preview.busy} disabled={!ready}>
                            Preview data
                        </Button>
                        <Button variant="outline-secondary" icon={["fad", "code"]} onClick={html.run} loading={html.busy} disabled={!ready}>
                            Render HTML
                        </Button>
                        <Button icon={["fad", "file-pdf"]} onClick={pdf.run} loading={pdf.busy} disabled={!ready}>
                            Render PDF
                        </Button>
                    </div>

                    {playwrightMissing && (
                        <Banner tone="warning" className="mt-3">
                            {Store.PLAYWRIGHT_HINT} HTML rendering still works without it.
                        </Banner>
                    )}

                    {[preview, html, pdf].map((a, i) =>
                        a.error && !playwrightMissing ? (
                            <Banner key={i} tone="danger" className="mt-3">
                                {a.error}
                            </Banner>
                        ) : null
                    )}
                </CardBody>
            </Card>

            {dataSet && (
                <Card>
                    <CardHeader>
                        <SectionHeader
                            title="Preview data"
                            description={`${dataSet.moduleName ?? ""}${dataSet.cycleName ? ` - ${dataSet.cycleName}` : ""} - ${
                                students.length
                            } student(s). Read straight from stored results.`}
                        />
                    </CardHeader>
                    <CardBody>
                        {students.length === 0 ? (
                            <EmptyState
                                icon={["fad", "file-lines"]}
                                title="No data for this selection"
                                hint={
                                    finalizedOnly
                                        ? "Nothing is finalized yet for these students. Untick 'finalized marks only' to see work in progress."
                                        : "No stored results exist for these students in this module."
                                }
                            />
                        ) : (
                            <div className="space-y-4">
                                {students.map((student) => (
                                    <div key={student.studentID} className="overflow-hidden rounded-md border border-border">
                                        <div className="border-b border-border bg-surface-muted px-4 py-2">
                                            <span className="font-medium text-text-strong">{nameOf(student.studentID)}</span>
                                            {nameOf(student.studentID) !== student.studentID && (
                                                <span className="ml-2">
                                                    <IdBadge id={student.studentID} />
                                                </span>
                                            )}
                                        </div>
                                        <Table>
                                            <Thead>
                                                <Tr>
                                                    <Th>Target</Th>
                                                    <Th>Mark type</Th>
                                                    <Th align="right">Result</Th>
                                                    <Th>Grade</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {(student.results ?? []).map((row: any, i: number) => (
                                                    <Tr key={i}>
                                                        <Td>{row.targetName ?? row.targetEntityID}</Td>
                                                        <Td>{row.markTypeName}</Td>
                                                        <Td align="right">
                                                            {row.result} / {row.total}
                                                        </Td>
                                                        <Td>
                                                            {row.gradeCode ? (
                                                                <Badge tone="primary" title={row.gradeName}>
                                                                    {row.gradeCode}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-text-weak">&mdash;</span>
                                                            )}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </>
    );
};

// ----------------------------------------------------------------- templates

interface TemplateDraft {
    name: string;
    description: string;
    bodyHtml: string;
    styles: string;
    paperSize: string;
    landscape: boolean;
}

const toDraft = (t?: ReportTemplateModel): TemplateDraft => ({
    name: t?.name ?? "",
    description: t?.description ?? "",
    bodyHtml: t?.bodyHtml ?? "",
    styles: t?.styles ?? "",
    paperSize: t?.paperSize ?? "A4",
    landscape: t?.landscape ?? false,
});

const TemplatesSection: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(Permissions.REPORTS_WRITE);

    const [editing, setEditing] = React.useState<{ mode: "create" | "edit"; template?: ReportTemplateModel } | null>(null);
    const [deleting, setDeleting] = React.useState<ReportTemplateModel | null>(null);

    const templates = useAsync(() => Store.listTemplates(true), [], { initial: [] as ReportTemplateModel[] });

    const remove = useAction(async () => {
        if (!deleting) return;
        await Store.deleteTemplate(deleting.id ?? "");
        toast.success("Template deleted.");
        setDeleting(null);
        templates.refresh();
    });

    return (
        <>
            <div className="mb-4 flex justify-end">
                {canManage && (
                    <Button icon={["fad", "plus"]} onClick={() => setEditing({ mode: "create" })}>
                        New template
                    </Button>
                )}
            </div>

            <Card>
                <ListStates
                    isLoading={templates.isLoading}
                    error={templates.error}
                    onRetry={templates.refresh}
                    isEmpty={(templates.data ?? []).length === 0}
                    empty={{
                        icon: ["fad", "file-code"],
                        title: "No report templates yet",
                        hint: canManage ? "Create one to define what a rendered report looks like." : undefined,
                    }}
                >
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Name</Th>
                                <Th>Description</Th>
                                <Th>Paper</Th>
                                <Th>Status</Th>
                                <Th>Updated</Th>
                                <Th align="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {(templates.data ?? []).map((template) => (
                                <Tr key={template.id}>
                                    <Td>
                                        <span className="font-medium text-text-strong">{template.name}</span>
                                    </Td>
                                    <Td>{template.description || <span className="text-text-weak">&mdash;</span>}</Td>
                                    <Td>
                                        <Badge tone="neutral">
                                            {template.paperSize}
                                            {template.landscape ? " landscape" : ""}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <StatusBadge status={template.isArchived ? "Archive" : "Enrolled"} />
                                    </Td>
                                    <Td>
                                        {template.updatedAt ? (
                                            new Date(template.updatedAt).toLocaleDateString()
                                        ) : (
                                            <span className="text-text-weak">&mdash;</span>
                                        )}
                                    </Td>
                                    <TdActions>
                                        {canManage && (
                                            <>
                                                <IconButton
                                                    icon={["fad", "pen-to-square"]}
                                                    label={`Edit ${template.name}`}
                                                    onClick={() => setEditing({ mode: "edit", template })}
                                                />
                                                {!template.isArchived && (
                                                    <IconButton
                                                        icon={["fad", "box-archive"]}
                                                        label={`Archive ${template.name}`}
                                                        onClick={async () => {
                                                            await Store.archiveTemplate(template.id ?? "");
                                                            toast.success("Template archived.");
                                                            templates.refresh();
                                                        }}
                                                    />
                                                )}
                                                <IconButton
                                                    icon={["fad", "trash"]}
                                                    variant="outline-danger"
                                                    label={`Delete ${template.name}`}
                                                    onClick={() => setDeleting(template)}
                                                />
                                            </>
                                        )}
                                    </TdActions>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </ListStates>
            </Card>

            <TemplateFormModal
                state={editing}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    templates.refresh();
                }}
            />

            <ConfirmModal
                isOpen={deleting !== null}
                onClose={() => setDeleting(null)}
                onConfirm={remove.run}
                title="Delete template"
                icon={["fad", "trash"]}
                confirmLabel="Delete"
                busy={remove.busy}
                error={remove.error}
                body={
                    <>
                        Delete <strong>{deleting?.name}</strong>? Reports already rendered from it are unaffected, but it cannot be used
                        again. This cannot be undone.
                    </>
                }
            />
        </>
    );
};

const TemplateFormModal: React.FC<{
    state: { mode: "create" | "edit"; template?: ReportTemplateModel } | null;
    onClose: () => void;
    onSaved: () => void;
}> = ({ state, onClose, onSaved }) => {
    const toast = useToast();
    const isOpen = state !== null;
    const isEdit = state?.mode === "edit";

    const [draft, setDraft] = React.useState<TemplateDraft>(toDraft);

    React.useEffect(() => {
        if (isOpen) setDraft(toDraft(state?.template));
    }, [isOpen, state]);

    const save = useAction(async () => {
        if (isEdit) {
            await Store.updateTemplate(state?.template?.id ?? "", draft);
            toast.success("Template updated.");
        } else {
            await Store.createTemplate(draft);
            toast.success("Template created.");
        }
        onSaved();
    });

    const set = (patch: Partial<TemplateDraft>) => setDraft((d) => ({ ...d, ...patch }));

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            title={isEdit ? "Edit template" : "New template"}
            icon={["fad", "file-code"]}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={save.busy}>
                        Cancel
                    </Button>
                    <Button onClick={save.run} loading={save.busy} disabled={!draft.name.trim()}>
                        {isEdit ? "Save changes" : "Create"}
                    </Button>
                </>
            }
        >
            <ModalBody>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Name" required className="mb-0">
                        <Input value={draft.name} placeholder="Term report" onChange={(e) => set({ name: e.target.value })} />
                    </Field>
                    <Field label="Paper size" className="mb-0">
                        <Select value={draft.paperSize} onChange={(e) => set({ paperSize: e.target.value })}>
                            {PAPER_SIZES.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </Select>
                    </Field>
                </div>

                <Field label="Description" className="mt-4">
                    <Input value={draft.description} onChange={(e) => set({ description: e.target.value })} />
                </Field>

                <Checkbox
                    className="mb-4"
                    label="Landscape"
                    checked={draft.landscape}
                    onChange={(e) => set({ landscape: e.target.checked })}
                />

                <Field
                    label="Body HTML"
                    hint="Rendered server-side against the report data set. It must not compute anything. Tokens: {{module}}, {{cycle}}, {{generated}}; inside {{#students}}…{{/students}} use {{student}} (the student's name) and {{studentID}}; inside {{#results}}…{{/results}} use {{entity}}, {{markType}}, {{result}}, {{total}}, {{grade}}, {{gradeName}}, {{origin}}, {{finalization}}, {{moderation}}."
                >
                    <Textarea rows={12} value={draft.bodyHtml} className="font-mono text-xs" onChange={(e) => set({ bodyHtml: e.target.value })} />
                </Field>

                <Field label="Styles" hint="CSS injected into the rendered document.">
                    <Textarea rows={6} value={draft.styles} className="font-mono text-xs" onChange={(e) => set({ styles: e.target.value })} />
                </Field>

                {save.error && <Banner tone="danger">{save.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

export default ReportsIndex;
